import { useTranslation } from "react-i18next";
import {
  ComposedModal,
  ModalBody,
  ModalHeader,
  StructuredListBody,
  StructuredListCell,
  StructuredListHead,
  StructuredListRow,
  StructuredListWrapper,
} from "@carbon/react";
import OcStatusPill from "../components/OcStatusPill";
import { formatOrderTotal } from "../formatOrderTotal";
import { PurchaseOrderSellerSummary } from "../../../types";
import { formatDate } from "../../../utils/utils";
import "./supplierOc.scss";

interface SupplierOcDetailModalProps {
  oc: PurchaseOrderSellerSummary | null;
  onClose: () => void;
}

/** Read-only view of a seller OC (there is no seller detail route): header terms + line items,
 * opened from the list's eye icon. Money stays in the OC's own currency. */
const SupplierOcDetailModal = ({ oc, onClose }: SupplierOcDetailModalProps) => {
  const { t } = useTranslation();
  if (!oc) return null;

  const meta: [string, string | null][] = [
    [t("purchaseOrders.supplier.buyer"), oc.buyerCompanyName],
    [
      t("purchaseOrders.supplier.requestedDelivery"),
      oc.requestedDeliveryDate ? formatDate(oc.requestedDeliveryDate) : null,
    ],
    [t("purchaseOrders.supplier.deliverySite"), oc.deliverySiteName],
    [t("purchaseOrders.supplier.paymentTerm"), oc.paymentTerm],
    [t("purchaseOrders.supplier.paymentMethod"), oc.paymentMethod],
    [t("purchaseOrders.supplier.incoterm"), oc.incoterm],
    [t("purchaseOrders.supplier.notes"), oc.notes],
  ];

  return (
    <ComposedModal
      open
      size="md"
      aria-label={t("purchaseOrders.supplier.detailTitle", { code: oc.code })}
      onClose={onClose}
    >
      <ModalHeader
        title={t("purchaseOrders.supplier.detailTitle", { code: oc.code })}
      />
      <ModalBody>
        <div className="supplier-oc-detail__status">
          <OcStatusPill status={oc.status} />
        </div>
        <dl className="supplier-oc-detail__meta">
          {meta
            .filter(([, value]) => value)
            .map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
        </dl>
        <StructuredListWrapper
          isCondensed
          aria-label={t("purchaseOrders.supplier.detailTitle", {
            code: oc.code,
          })}
        >
          <StructuredListHead>
            <StructuredListRow head>
              <StructuredListCell head>
                {t("purchaseOrders.supplier.lineMaterial")}
              </StructuredListCell>
              <StructuredListCell head>
                {t("purchaseOrders.supplier.lineQty")}
              </StructuredListCell>
              <StructuredListCell head>
                {t("purchaseOrders.supplier.lineUnitPrice")}
              </StructuredListCell>
              <StructuredListCell head>
                {t("purchaseOrders.supplier.lineTotal")}
              </StructuredListCell>
            </StructuredListRow>
          </StructuredListHead>
          <StructuredListBody>
            {oc.lines.map((line, index) => (
              <StructuredListRow key={index}>
                <StructuredListCell>
                  {line.materialName ?? "-"}
                  {line.description ? ` — ${line.description}` : ""}
                </StructuredListCell>
                <StructuredListCell>
                  {line.quantity}
                  {line.unit ? ` ${line.unit}` : ""}
                </StructuredListCell>
                <StructuredListCell>
                  {formatOrderTotal(line.unitPrice, oc.currency)}
                </StructuredListCell>
                <StructuredListCell>
                  {formatOrderTotal(line.lineTotal, oc.currency)}
                </StructuredListCell>
              </StructuredListRow>
            ))}
          </StructuredListBody>
        </StructuredListWrapper>
        <p className="supplier-oc-detail__total">
          <span>{t("purchaseOrders.supplier.total")}</span>
          <strong>{formatOrderTotal(oc.totalAmount, oc.currency)}</strong>
        </p>
      </ModalBody>
    </ComposedModal>
  );
};

export default SupplierOcDetailModal;
